import { Fragment, useState, useEffect, useCallback, useMemo } from 'react'
import { useModal, Spinner, Icon, Text, Stack } from '@leroy-merlin-br/backyard-react-ui'
import {
  CheckmarkCircleSolid,
  AlertTriangleSolid
} from '@leroy-merlin-br/backyard-react-icons'

import emitter from 'garden/src/js/utils/emitter'

import { AlertMessage } from 'reactShared/components/AlertMessage'

import { formatters } from 'reactUtils/formatters'

import { getRegionFromPostalCode } from 'catalog/services/region'
import { getShipments } from 'catalog/services/shipments'
import { ShipmentsOptions } from 'catalog/pages/Product/components/ShipmentsOptions'
import { RegionSelect } from 'catalog/pages/Product/components/RegionSelect'
import { useProfile } from 'catalog/hooks/use-profile'
import { useShipmentsDataLayer } from 'catalog/hooks/use-shipments-data-layer'

import { ErrorNotification } from './components/ErrorNotification'
import { MainShipmentPrices } from './components/MainShipmentPrices'
import * as S from './styled'

const StatusIcon = ({ status }) => {
  if (status === 'success') {
    return <Icon as={CheckmarkCircleSolid} color="green" data-testid="success-icon" />
  }

  if (status === 'loading') {
    return <Spinner data-testid="loading-icon" />
  }

  return <Icon as={AlertTriangleSolid} data-testid="danger-icon" />
}

const PostalCode = ({ productId, purchaseButtons, skus, isLespaceProduct }) => {
  const { setModal } = useModal()

  const parsedSkus = useMemo(() => JSON.parse(skus), [skus])
  const buttons = useMemo(() => JSON.parse(purchaseButtons), [purchaseButtons])
  const isDisabled = useMemo(() => !buttons.ecommerce.enabled, [buttons])
  const isPickupEnabled = useMemo(() => buttons.pickupInStore.enabled, [buttons])

  const { profile } = useProfile()

  const [status, setStatus] = useState(null)
  const [postalCode, setPostalCode] = useState('')
  const [shipments, setShipments] = useState([])
  const [errorStatus, setErrorStatus] = useState()
  const [regionData, setRegionData] = useState()

  const pushShipmentsToDataLayer = useShipmentsDataLayer(
    postalCode,
    regionData,
    shipments,
    errorStatus,
    parsedSkus
  )

  const fetchShipments = useCallback(
    (zipCode, region) => {
      const fetch = async () => {
        try {
          const shipments = await getShipments(productId, zipCode, region)

          setShipments(shipments)
          setStatus('success')
        } catch (error) {
          setErrorStatus(error.status)
          setStatus('danger')
        }
      }

      fetch()
    },
    [productId]
  )

  const onPostalCodeChange = e => {
    e.stopPropagation()

    const inputPostalCode = formatters.cep(e.target.value)

    if (inputPostalCode.length <= 9) {
      setPostalCode(inputPostalCode)
      setStatus('danger')
    }

    if (inputPostalCode.length === 0) {
      setStatus(null)
      setErrorStatus(0)
      setShipments([])
    }
  }

  const closeRegionSelectModal = useCallback(() => {
    setStatus(null)
    setShipments([])
    setErrorStatus(0)
    setPostalCode('')
  }, [])

  const showRegionSelectModal = useCallback(
    ({ postalCode, region }) => {
      setModal({
        appElement: 'body',
        onClose: closeRegionSelectModal,
        children: ({ onClose }) => (
          <RegionSelect
            onClose={onClose}
            postalCode={postalCode}
            region={region}
          />
        )
      })
    },
    [closeRegionSelectModal, setModal]
  )

  useEffect(() => {
    pushShipmentsToDataLayer()
  }, [pushShipmentsToDataLayer])

  useEffect(() => {
    if (shipments || errorStatus) {
      emitter.emit('shipments:change', {
        shipments,
        shipmentsErrorStatus: errorStatus
      })
    }
  }, [shipments, errorStatus])

  useEffect(() => {
    if (!isDisabled && profile.zipCode) {
      const setPostalCodeFromUserResources = async () => {
        const response = await getRegionFromPostalCode(profile.zipCode)

        if (response.region === profile.region) {
          setPostalCode(profile.zipCode)
        }
      }

      setPostalCodeFromUserResources()
    }
  }, [isDisabled, profile, fetchShipments])

  useEffect(() => {
    if (postalCode.length <= 8) {
      return
    }

    setErrorStatus(0)
    setShipments([])
    setStatus('loading')

    const getRegionAndShipments = async () => {
      try {
        const result = await getRegionFromPostalCode(postalCode)

        if (result.region === profile.region) {
          setRegionData(result)
          fetchShipments(postalCode, result.region)

          return
        }

        setStatus(null)

        showRegionSelectModal({ postalCode, region: result })
      } catch (error) {
        setStatus('danger')
      }
    }

    getRegionAndShipments()
  }, [postalCode, fetchShipments, profile.region, showRegionSelectModal])

  if (isLespaceProduct) {
    return (
      <AlertMessage type="info">
        Esse produto faz parte da{' '}
        <Text as="strong" size="kilo" bold noMargin>
          gama exclusiva da loja L’Espace.{' '}
        </Text>
        Para consultar o preço do frete, adicione o produto ao carrinho!
      </AlertMessage>
    )
  }

  return (
    <S.PostalCode status={status}>
      <S.Label>Calcule seu frete</S.Label>
      <S.Input
        name="postal-code"
        placeholder="Insira seu CEP"
        value={postalCode}
        onChange={onPostalCodeChange}
        value={postalCode}
        disabled={isDisabled}
      />
      {status && <StatusIcon status={status} />}

      <S.Link
        href="http://www.buscacep.correios.com.br/sistemas/buscacep/"
        target="_blank"
        rel="noopener noreferrer"
        as={isDisabled ? 'span' : 'a'}
        isDisabled={isDisabled}
      >
        Não sei meu CEP
      </S.Link>

      <Stack spacing="kilo">
        {shipments.length === 1 && parsedSkus.length === 1 && (
          <Fragment>
            <S.Separator />
            <MainShipmentPrices {...shipments[0]} isPickupEnabled={isPickupEnabled} />
          </Fragment>
        )}

        {shipments.length > 0 && regionData && (
          <Fragment>
            {parsedSkus.length > 1 && <S.Separator />}
            <ShipmentsOptions
              shipments={shipments}
              skus={parsedSkus}
              region={regionData}
            />
          </Fragment>
        )}
      </Stack>

      {(shipments.length > 0 ||
        errorStatus > 0 ||
        isDisabled) && <S.Separator />}

      {shipments.length > 0 && (
        <S.InformativeMessage size="kilo" noMargin>
          <Text as="strong" size="kilo" bold noMargin>Atenção! </Text>
          Prazo e valores de frete
          <Text as="strong" size="kilo" bold noMargin> podem variar </Text>
          conforme a quantidade e disponibilidade dos itens adicionados ao seu
          carrinho. O tempo de entrega se
          <Text as="strong" size="kilo" bold noMargin> inicia após a confirmação do seu pagamento</Text>.
        </S.InformativeMessage>
      )}

      {errorStatus > 0 && <ErrorNotification errorStatus={errorStatus} />}

      {isDisabled && (
        <S.InformativeMessage size="kilo" isDisabled noMargin>
          <Text as="strong" size="kilo" bold noMargin>Atenção! </Text>
          Este produto não está disponível para entrega e por isso a calculadora
          de frete está desativada.
        </S.InformativeMessage>
      )}
    </S.PostalCode>
  )
}

export default PostalCode
